import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../../servizi/api.ts'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Scelta } from '../../componenti/base.tsx'
import { Guscio, conteggio, messaggioErrore, usaElenco } from '../../componenti/elenco.tsx'
import {
  ChiudiRicontatto, EtichettaStato, ModuloRicontatto, quandoRicontatto,
  type RicontattiScheda, type Ricontatto,
} from '../../componenti/ricontatti.tsx'

/**
 * Ricontatti e appuntamenti (465): quelli che segue chi guarda, oppure tutti quelli delle schede che
 * puo' gestire, per stato. Si fissano dalla scheda della persona («Fissa un ricontatto») o insieme
 * a un intervento; qui si modificano e si chiudono con l'esito.
 */

type Riga = Ricontatto & { nome: string; cognome: string; numero_scheda: number }

const STATI = [
  { valore: 'programmato', etichetta: 'In programma' },
  { valore: 'fatto', etichetta: 'Fatti' },
  { valore: 'non_presentata', etichetta: 'La persona non si è presentata' },
  { valore: 'annullato', etichetta: 'Annullati' },
  { valore: 'scaduto', etichetta: 'Rimasti senza esito' },
]
const CHI = [
  { valore: 'true', etichetta: 'Quelli che seguo io' },
  { valore: 'false', etichetta: 'Tutti quelli che posso vedere' },
]

export function FollowUp() {
  const [stato, setStato] = useState('programmato')
  const [mie, setMie] = useState('true')
  const [esito, setEsito] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [chiusura, setChiusura] = useState<Riga | null>(null)
  const [modifica, setModifica] = useState<{ riga: Riga; dati: RicontattiScheda } | null>(null)
  const percorso = useMemo(() => `/follow-up?${new URLSearchParams({ stato, mie })}`, [stato, mie])
  const { dati, errore, ricarica, finestra } = usaElenco<Riga>(percorso)
  const aperti = stato === 'programmato' || stato === 'scaduto'

  const fatto = (testo: string) => { setChiusura(null); setModifica(null); setEsito(testo); ricarica() }

  /** La modifica ha bisogno dei colleghi e dei percorsi della scheda. */
  async function apriModifica(riga: Riga) {
    setEsito(null); setErroreAzione(null)
    try {
      const d = await api.get<RicontattiScheda>(`/persone/${riga.persona_id}/ricontatti`)
      if (!d.puo_gestire) { setErroreAzione('Non puoi più gestire i ricontatti di questa persona.'); return }
      setModifica({ riga, dati: d })
    } catch (e) {
      setErroreAzione(messaggioErrore(e))
    }
  }

  return (
    <>
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}
      <Guscio titolo="Ricontatti" errore={errore} dati={dati} finestra={finestra}
              sottotitolo={conteggio(finestra.totale, 'ricontatto', 'ricontatti')}
              vuoto={stato === 'programmato'
                ? 'Nessun ricontatto in programma. Si fissano dalla scheda della persona, con «Fissa un ricontatto».'
                : 'Nessun ricontatto.'}
              azioni={<>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Quali" valore={stato} cambia={setStato} opzioni={STATI} />
                </div>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Di chi" valore={mie} cambia={setMie} opzioni={CHI} />
                </div>
              </>}
              intestazioni={['Quando', 'Beneficiario', 'Motivo', 'Chi lo segue', 'Stato',
                             <span className="solo-lettore">Comandi</span>]}>
        {dati?.map((f) => (
          <tr key={f.id}>
            <td data-etichetta="Quando">{quandoRicontatto(f)}</td>
            <td data-etichetta="Beneficiario"><Link to={`/persone/${f.persona_id}`}>{f.cognome} {f.nome}</Link></td>
            <td data-etichetta="Motivo" className="testo-libero">
              {f.motivo}
              {f.esito && <div className="piccolo tenue">{f.esito}</div>}
            </td>
            <td data-etichetta="Chi lo segue">{f.responsabile ?? '—'}</td>
            <td data-etichetta="Stato"><EtichettaStato r={f} /></td>
            <td data-etichetta="Comandi">
              {aperti && (
                <div className="riga">
                  <Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setChiusura(f) }}>
                    Com’è andato?
                  </Bottone>
                  <Bottone variante="discreto" dimensione="piccolo" onClick={() => void apriModifica(f)}>
                    Modifica
                  </Bottone>
                </div>
              )}
            </td>
          </tr>
        ))}
      </Guscio>

      {chiusura && <ChiudiRicontatto ricontatto={chiusura} chiudi={() => setChiusura(null)} fatto={fatto} />}
      {modifica && (
        <ModuloRicontatto personaId={modifica.riga.persona_id} ricontatto={modifica.riga} dati={modifica.dati}
                          chiudi={() => setModifica(null)} fatto={fatto} />
      )}
    </>
  )
}
